/**
 * A free camera for looking at the game from outside it.
 *
 * The player's viewpoint is bolted to the observation deck, which is correct for the game
 * and useless for judging what has been built: you cannot see the platform's silhouette,
 * you cannot get above a creature, and you cannot check what a structure does from an
 * angle nobody will ever stand at. Every inspection so far has been me guessing camera
 * coordinates in a script and rendering blind.
 *
 * Enabled only by `?fly`. It takes over the camera completely — the game's own look
 * controls are detached and the simulation is paused — so there is no way for this to
 * affect a real session.
 *
 * Controls are deliberately the ones every 3D tool already uses, so there is nothing to
 * learn: drag to look, WASD to move, R/F for up and down, shift to sprint.
 */

import type { RenderWorld } from '../render/world';
import type { WeatherPresetName } from '../sim/weather';
import { WEATHER_PRESETS } from '../sim/weather';

const WEATHER_CYCLE: WeatherPresetName[] = [
  'clearDay', 'overcastDay', 'fogBank', 'dusk', 'night', 'storm', 'nightStorm',
  'severeStorm', 'dawnFog',
];

export interface FlycamOptions {
  world: RenderWorld;
  canvas: HTMLCanvasElement;
  /** Called each frame with dt in seconds, so the caller keeps owning the render loop. */
  onFrame: (cb: () => void) => () => void;
}

interface FlycamSave {
  x: number; y: number; z: number;
  yaw: number; pitch: number;
  speed: number; weather: number;
}

/** Last camera state, or null when there is none or it is unreadable. */
function readSaved(): FlycamSave | null {
  try {
    const raw = localStorage.getItem('deepwater.flycam');
    if (!raw) return null;
    const v = JSON.parse(raw) as FlycamSave;
    // Guard every field: a half-written or hand-edited value must not strand the camera
    // somewhere it cannot be flown back from.
    if (![v.x, v.y, v.z, v.yaw, v.pitch].every(Number.isFinite)) return null;
    return v;
  } catch {
    return null;
  }
}

export function mountFlycam(opts: FlycamOptions): () => void {
  const { world, canvas } = opts;
  const camera = world.camera;

  /*
   * Respawn exactly where you left off.
   *
   * A fixed start meant flying back to the same structure after every reload, and this
   * tool exists to be used across dozens of reloads while something is being iterated on.
   * Position, heading and weather are all restored; a bad state is one keypress from
   * fixed, since the presets are absolute.
   */
  const SAVE_KEY = 'deepwater.flycam';
  const saved = readSaved();
  camera.position.set(
    saved?.x ?? 95,
    saved?.y ?? 150,
    saved?.z ?? -110,
  );
  let yaw = saved?.yaw ?? -0.70;
  let pitch = saved?.pitch ?? 0.36;
  /*
   * Slower by default, and finer to adjust.
   *
   * 40 m/s is a reconnaissance speed — useful for crossing to the far side of the rig,
   * useless for lining up on a single fixture, which is what this tool is mostly used
   * for. The floor drops to 0.5 m/s so a lamp can be approached at a crawl.
   */
  let speed = saved?.speed ?? 9;
  let weatherIndex = saved?.weather ?? 0;

  const held = new Set<string>();
  let dragging = false;



  const apply = (): void => {
    camera.rotation.set(pitch, yaw, 0);
  };
  apply();

  // --- Overlay ---------------------------------------------------------------------
  const ui = document.createElement('div');
  ui.id = 'flycam';
  ui.innerHTML = `
    <div class="fc-readout">
      <b>FLYCAM</b>
      <span id="fcPos">—</span>
      <span id="fcAim">—</span>
      <span id="fcAlt">—</span>
      <span id="fcSpeed">—</span>
      <span id="fcWeather">—</span>
    </div>
    <div class="fc-help">
      <b>drag</b> look &nbsp; <b>WASD</b> move &nbsp; <b>R/F</b> up·down &nbsp;
      <b>shift</b> sprint &nbsp; <b>wheel</b> speed<br/>
      <b>[ ]</b> weather &nbsp; <b>1–5</b> presets &nbsp; <b>P</b> screenshot &nbsp;
<b>H</b> hide this
    </div>
  `;
  document.body.appendChild(ui);
  const posEl = ui.querySelector('#fcPos') as HTMLElement;
  const aimEl = ui.querySelector('#fcAim') as HTMLElement;
  const altEl = ui.querySelector('#fcAlt') as HTMLElement;
  const speedEl = ui.querySelector('#fcSpeed') as HTMLElement;
  const weatherEl = ui.querySelector('#fcWeather') as HTMLElement;

  const setWeather = (index: number): void => {
    weatherIndex = (index + WEATHER_CYCLE.length) % WEATHER_CYCLE.length;
    const name = WEATHER_CYCLE[weatherIndex] as WeatherPresetName;
    world.setWeather(WEATHER_PRESETS[name]);
    weatherEl.textContent = name;
  };
  // Restore the saved weather, not the first in the cycle.
  setWeather(weatherIndex);

  /**
   * Save what is on screen.
   *
   * Rendered fresh rather than read from the existing buffer: the WebGL context is
   * created without `preserveDrawingBuffer`, so by the time a click handler runs the
   * back buffer has usually been cleared and the PNG comes out empty.
   */
  const screenshot = (): void => {
    world.render();
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const w = WEATHER_CYCLE[weatherIndex];
      const p = camera.position;
      a.href = url;
      a.download = `deepwater-${w}-${Math.round(p.x)}_${Math.round(p.y)}_${Math.round(p.z)}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  // --- Input -----------------------------------------------------------------------
  const onKeyDown = (e: KeyboardEvent): void => {
    held.add(e.code);
    if (e.code === 'KeyP') { screenshot(); e.preventDefault(); }
    if (e.code === 'BracketRight') setWeather(weatherIndex + 1);
    if (e.code === 'BracketLeft') setWeather(weatherIndex - 1);
    if (e.code === 'KeyH') ui.classList.toggle('hidden');
    // Four vantage points worth returning to, so a comparison shot is repeatable.
    if (e.code === 'Digit1') { camera.position.set(95, 150, -110); yaw = -0.70; pitch = 0.36; apply(); }
    // 5 pulls back out to the establishing distance the aerials were shot from.
    if (e.code === 'Digit5') { camera.position.set(230, 210, -260); yaw = -0.72; pitch = 0.42; apply(); }
    if (e.code === 'Digit2') { camera.position.set(0, 620, -90); yaw = -0.35; pitch = 1.25; apply(); }
    if (e.code === 'Digit3') { camera.position.set(430, 120, 40); yaw = -1.47; pitch = 0.16; apply(); }
    if (e.code === 'Digit4') { camera.position.set(2, 105, 3); yaw = -0.23; pitch = 0.14; apply(); }
  };
  const onKeyUp = (e: KeyboardEvent): void => { held.delete(e.code); };

  const onDown = (e: PointerEvent): void => {
    dragging = true;
    canvas.setPointerCapture(e.pointerId);
  };
  const onUp = (): void => { dragging = false; };
  const onMove = (e: PointerEvent): void => {
    if (!dragging) return;
    // Roughly half the previous rate: framing a 0.7 m lens from a few metres away was
    // overshooting on the smallest mouse movement.
    yaw += e.movementX * 0.0016;
    // Positive camera pitch looks DOWN in Babylon, so dragging up must increase it.
    pitch += e.movementY * 0.0016;
    pitch = Math.max(-1.5, Math.min(1.5, pitch));
    apply();
  };
  const onWheel = (e: WheelEvent): void => {
    // Smaller steps, and a much lower floor, so the wheel can find 1 m/s rather than
    // jumping past it.
    speed = Math.max(0.5, Math.min(400, speed * (e.deltaY > 0 ? 0.93 : 1.075)));
    e.preventDefault();
  };

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  canvas.addEventListener('pointerdown', onDown);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointermove', onMove);
  canvas.addEventListener('wheel', onWheel, { passive: false });

  // --- Per-frame -------------------------------------------------------------------
  let last = performance.now();
  let saveTimer = 1;
  const off = opts.onFrame(() => {
    const now = performance.now();
    const dt = Math.min(0.1, (now - last) / 1000);
    last = now;

    const boost = held.has('ShiftLeft') || held.has('ShiftRight') ? 5 : 1;
    const step = speed * boost * dt;

    // Forward is where the camera looks; strafe is perpendicular in the ground plane.
    const cp = Math.cos(pitch);
    const fx = Math.sin(yaw) * cp;
    const fy = -Math.sin(pitch);
    const fz = Math.cos(yaw) * cp;
    const sx = Math.cos(yaw);
    const sz = -Math.sin(yaw);

    if (held.has('KeyW')) camera.position.addInPlaceFromFloats(fx * step, fy * step, fz * step);
    if (held.has('KeyS')) camera.position.addInPlaceFromFloats(-fx * step, -fy * step, -fz * step);
    if (held.has('KeyD')) camera.position.addInPlaceFromFloats(sx * step, 0, sz * step);
    if (held.has('KeyA')) camera.position.addInPlaceFromFloats(-sx * step, 0, -sz * step);
    if (held.has('KeyR')) camera.position.y += step;
    if (held.has('KeyF')) camera.position.y -= step;

    // Throttled: writing localStorage at 60 Hz would be absurd, and a second of lost
    // movement after a crash costs nothing.
    saveTimer -= dt;
    if (saveTimer <= 0) {
      saveTimer = 1;
      try {
        localStorage.setItem(SAVE_KEY, JSON.stringify({
          x: camera.position.x, y: camera.position.y, z: camera.position.z,
          yaw, pitch, speed, weather: weatherIndex,
        } satisfies FlycamSave));
      } catch { /* private mode, or storage full. Not worth interrupting for. */ }
    }

    const p = camera.position;
    posEl.textContent = `${p.x.toFixed(0)} ${p.y.toFixed(0)} ${p.z.toFixed(0)} m`;
    /*
     * Heading matters as much as position. A screenshot with coordinates but no bearing
     * cannot be mapped back onto the build — which is exactly how "name these three
     * structures" became unanswerable.
     */
    const yawDeg = ((yaw * 180) / Math.PI) % 360;
    aimEl.textContent = `yaw ${yawDeg.toFixed(0)}° pitch ${((pitch * 180) / Math.PI).toFixed(0)}°`;
    // Height above the waterline, named as such. `p.y` alone reads as an abstract
    // coordinate; "alt" says what it is measured from.
    altEl.textContent = `alt ${p.y.toFixed(0)} m`;
    speedEl.textContent = `${speed.toFixed(0)} m/s${boost > 1 ? ' ×5' : ''}`;
  });

  return () => {
    off();
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    canvas.removeEventListener('pointerdown', onDown);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointermove', onMove);
    canvas.removeEventListener('wheel', onWheel);
    ui.remove();
  };
}
